<template>
  <div class="pagination">
    <a-button
      :disabled="pageIndex <= 0"
      aria-label="Previous page"
      type="button"
      :prefix="previousPrefix"
      :suffix="previousSuffix"
      :class="buttonClass"
      :look="buttonLook"
      :variant="buttonVariant"
      @click="previousPage()">
      Previous
    </a-button>

    <div class="pagination__page-number-wrapper">
      <span
        v-for="(page, index) in availablePages"
        :key="index"
        :class="pageNumberClass(page)"
        @click="selectPage(page)">
        {{ page }}
      </span>
    </div>

    <a-button
      :disabled="pageIndex >= pageCount - 1"
      aria-label="Next page"
      :prefix="nextPrefix"
      :suffix="nextSuffix"
      :class="buttonClass"
      :look="buttonLook"
      :variant="buttonVariant"
      type="button"
      @click="nextPage()">
      Next
    </a-button>
  </div>
</template>

<script>
export default {
  name: "SearchPagination",
  props: {
    pageCount: {
      type: Number,
      default: 0,
    },
    pageIndex: {
      type: Number,
      default: 0,
    },
    previousPrefix: {
      type: String,
      default: null,
    },
    previousSuffix: {
      type: String,
      default: null,
    },
    nextPrefix: {
      type: String,
      default: null,
    },
    nextSuffix: {
      type: String,
      default: null,
    },
    buttonClass: {
      type: String,
      default: undefined,
    },
    buttonVariant: {
      type: String,
      default: undefined,
    },
    buttonLook: {
      type: String,
      default: undefined,
    },
  },
  emits: ["next", "prev", "change-page"],
  data () {
    return {
      selectedPage: 0,
      pagePadding: 2,
    };
  },
  computed: {
    pageNumber () {
      return this.pageIndex + 1;
    },
    maxPagePadding () {
      return ( this.pagePadding * 2 ) + 1;
    },
    min () {
      const pagesLeft = this.pageCount - this.pageNumber;
      const totalPadding = this.maxPagePadding - this.pagePadding;
      const endPadding = Math.max( 0, totalPadding - pagesLeft - 1 );

      return Math.max( 1, this.pageNumber - this.pagePadding - endPadding );
    },
    max () {
      const startPadding = Math.abs(
        Math.min( 0, this.pageNumber - this.maxPagePadding + this.pagePadding ) );
      return Math.min( this.pageCount, this.pageNumber + this.pagePadding + startPadding );
    },
    availablePages () {
      const result = this.fillArray( this.min, this.max );

      // Only worry about adding ellipses if there are enough pages
      if ( this.pageCount + 1 >= this.pagePadding + this.maxPagePadding ) {
        // If we are not near the end, we might want ellipses on the right
        const rightPaddedPage = this.pageNumber + this.pagePadding;
        if ( rightPaddedPage <= this.pageCount - 1 ) {
          const distance = Math.abs( result[result.length - 1] - this.pageCount );
          const willOmitPages = rightPaddedPage + 1 <= this.pageCount - 1;

          if ( willOmitPages && distance === 2 ) {
            result.push( String( this.pageCount - 1 ) );
          } else if ( willOmitPages && distance > 2 ) {
            result.push( "..." );
          }

          result.push( this.pageCount );
        }

        // If we are not near the beginning, we might want ellipses on the left
        const leftPaddedPage = this.pageNumber - this.pagePadding;
        if ( leftPaddedPage > 1 ) {
          const distance = Math.abs( result[0] - 1 );
          const willOmitPages = leftPaddedPage + 1 > 1;

          result.splice( 0, 0, "1" );

          if ( willOmitPages && distance === 2 ) {
            result.splice( 1, 0, "2" );
          } else if ( willOmitPages && distance > 2 ) {
            result.splice( 1, 0, "..." );
          }
        }
      }

      return result;
    },
  },
  watch: {
    pageIndex: {
      immediate: true,
      handler ( val ) {
        this.selectedPage = val + 1;
      },
    },
    selectedPage ( val ) {
      this.selectPage( val );
    },
  },
  methods: {
    fillArray ( min, max ) {
      const length = ( max - min ) + 1;
      return Array.from( new Array( length ), ( _, i ) => String( min + i ) );
    },
    pageNumberClass ( number ) {
      let result = "";

      if ( number === "..." ) {
        result = "pagination__ellipsis";
      } else if ( Number.parseInt( number, 10 ) === this.pageNumber ) {
        result = "pagination__page-number pagination__page-number--selected";
      } else {
        result = "pagination__page-number";
      }

      return result;
    },
    nextPage () {
      this.$emit( "next" );
    },
    previousPage () {
      this.$emit( "prev" );
    },
    changePage ( pageIndex ) {
      this.$emit( "change-page", pageIndex );
    },
    selectPage ( value ) {
      if ( value !== "..." ) {
        this.selectedPage = value;
        this.changePage( value - 1 );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination {
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 767px) {
    padding-left: 0;
    padding-right: 0;
  }

  &__page-number-wrapper {
    margin: 0 15px;
    padding: 5px 15px;
    display: flex;
    z-index: 10;

    @media (max-width: 767px) {
      display: none;
    }
  }

  &__current-page,
  &__page-number--selected {
    border-color: transparent !important;
    background: none !important;
  }

  &__page-number,
  &__current-page {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    text-align: center;
    margin: 0 5px;
    padding: 5px 15px;
    min-width: 31px;
    min-height: 31px;
    border: 2px solid #ccc;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 5px;

    @media (max-width: 767px) {
      font-size: 1rem;
      margin: 0;
    }

    &:hover {
      cursor: pointer;
      background: rgba(255, 255, 255, 0.25);
      text-decoration: none;
    }
  }

  &__ellipsis {
    min-width: 31px;
    padding-left: 5px;
    padding-right: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  button {
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #fff;

    @media (min-width: 768px) {
      min-width: 120px;
    }

    @media (max-width: 767px) {
      font-size: 1rem;
      flex-grow: 1;
      flex-basis: 50%;
      background: rgba(0, 0, 0, 0.05);
      margin-right: 5px;

      &:last-child {
        margin-right: 0;
      }
    }

    .icon {
      margin: 0 5px;
    }
  }

  .select-wrapper {
    min-width: 80px;
    display: inline-block;
    margin: 0 10px;
  }
}
</style>
